/* eslint-disable */
import { memo, useState, useEffect, useMemo, useLayoutEffect, useRef } from "react";
import { getIn, FormikProps } from "formik";
import CustomDatePicker from "../../../../../shared/CustomDatePicker";
import CustomRadioButton from "../../../../../shared/CustomRadioButton";
import CustomSelect from "../../../../../shared/CustomSelect";
import CustomTextField from "../../../../../shared/CustomTextField";
import {
  OPTIONS,
  findKeysByValue
} from "../../../../../helpers/FormDetails/ProposalDetails/resources";
import { OPTIONS as NationalityOptions } from "../../../../../helpers/Dashboard/resources";

import { IJointLifeAddendum } from "src/helpers/FormDetails/JointLifeAddendum/interfaces";
import Tooltip from "@mui/material/Tooltip";
import CustomAutocomplete from "src/shared/CustomAutocomplete";
import { useMutation } from "react-query";
import { countryCall, subOccupationCall } from "src/services/Master";
import { useSelector } from "react-redux";
import { useAppSelector } from "src/utils/hooks/useAppSelector";
import store from "src/redux/store";
import {
  setJointSubOccupationData,
  setJointCountryData
} from "src/redux/Actions/jointLifeAddendum";
import {
  handelIndianCurrencyChange,
  handleUppercaseChange,
  onChangeIndianCurrency
} from "src/helpers/FormDetails";
import dayjs from "dayjs";
import { PRODUCT_ID } from "src/helpers/FormDetails/ProposalDetails/interfaces";
import { partners } from "src/config/constant";
import CustomCheckBox from "src/shared/CustomCheckBox";

interface Props {
  formik: FormikProps<IJointLifeAddendum>;
  memberDetailsSchema: any;
}

interface MasterData {
  [key: string]: string;
}

interface ApiResponseItem {
  name: string;
  code: string;
}

const combinedSelector = (state: {
  masterdata_reducer: {
    genderDetails: MasterData;
    educationDetails: MasterData;
    jointRelationshipDetails: MasterData;
    occupationDetails: MasterData;
    salutationDetails: MasterData;
    maritalstatusDetails: MasterData;
  };
}) => {
  const {
    genderDetails,
    educationDetails,
    jointRelationshipDetails,
    occupationDetails,
    salutationDetails,
    maritalstatusDetails
  } = state.masterdata_reducer;

  return {
    genderDataList: Object.values(genderDetails),
    educationDataList: Object.values(educationDetails),
    relationDataList: Object.values(jointRelationshipDetails),
    occupationDataList: Object.values(occupationDetails),
    salutationDataList: Object.values(salutationDetails),
    maritalStatusDataList: Object.values(maritalstatusDetails)
  };
};
function JointMemberDetails({
  formik: { errors, handleBlur, setFieldTouched, touched, values, setFieldValue, handleChange },
  memberDetailsSchema
}: Props) {
  const {
    genderDataList,
    educationDataList,
    // relationDataList,
    occupationDataList,
    maritalStatusDataList
    // subOccupationDataList
  } = useSelector(combinedSelector);
  const prevOccupationRef = useRef(values.member?.occupationCode);
  const [subOccupationOptions, setSubOccupationOptions] = useState<string[]>([]);
  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const masterData = useAppSelector((state) => state.masterdata_reducer);
  const jointLifeMemberDetails = useAppSelector((state) => state.jointLifeAddendum.member);
  const productId = useAppSelector((state) => state.common.productId);
  const partnerId = useAppSelector((state) => state.common?.leadDetails?.partnerId);

  const { mutate: mutateSubOccupation } = useMutation(subOccupationCall, {
    retry: false,
    onSuccess: async (response: { data: ApiResponseItem[] }, variables) => {
      const subOccupationArray = response.data.map((item) => item.name);
      setSubOccupationOptions(subOccupationArray);
      if (response.data?.length) {
        const exactData = response.data.find((item) => item?.name === variables?.name);
        if (exactData) {
          setFieldValue("member.subOccupationCode", exactData.code);
          store.dispatch(setJointSubOccupationData(exactData));
        }
      }
    }
  });

  const { mutate: mutateCountry } = useMutation(countryCall, {
    retry: false,
    onSuccess: async (response: { data: ApiResponseItem[] }, variables) => {
      const countryArray = response.data.map((item) => item.name);
      setCountryOptions(countryArray);

      if (response.data?.length) {
        const exactData = response.data.find((item) => item?.name === variables?.name);
        if (exactData) {
          setFieldValue("member.countryOfResidenceCode", exactData.code);
          store.dispatch(setJointCountryData(exactData));
        }
      }
    }
  });

  const handleSubOccupationChange = async (value: string) => {
    // console.log("entering suboccupation");
    await setFieldValue("member.subOccupation", value);
    if (value) mutateSubOccupation({ name: value });
  };

  const handleCountryChange = async (value: string) => {
    // console.log("entering country");
    await setFieldValue("member.countryOfResidence", value);
    if (value) mutateCountry({ name: value });
  };

  useEffect(() => {
    if (!masterData?.salutationDetails || !values.member?.genderCode) return;

    const genderCode = values.member.genderCode;
    const genderSpecificTitles = masterData.salutationDetails[genderCode];

    if (genderSpecificTitles?.length) {
      const currentTitle = values.member?.title;
      const isValidTitle = genderSpecificTitles.some(
        (item: { label: string }) => item.label === currentTitle
      );

      if (!currentTitle || !isValidTitle) {
        const defaultTitle = genderSpecificTitles[0].label;
        setFieldValue(
          "member.title",
          jointLifeMemberDetails?.title ? jointLifeMemberDetails?.title : defaultTitle
        );
        setFieldValue(
          "member.titleCode",
          jointLifeMemberDetails?.titleCode
            ? jointLifeMemberDetails?.titleCode
            : genderSpecificTitles[0].value
        );
      }
    }
  }, [values.member?.genderCode, masterData?.salutationDetails, setFieldValue]);

  useEffect(() => {
    setFieldValue(
      "member.maritalStatusCode",
      findKeysByValue(masterData.maritalstatusDetails, values.member?.maritalStatus)
    );
  }, [values.member?.maritalStatus]);

  useEffect(() => {
    setFieldValue(
      "member.educationQualificationCode",
      findKeysByValue(masterData.educationDetails, values.member?.educationQualification)
    );
  }, [values.member?.educationQualification]);

  useEffect(() => {
    setFieldValue(
      "member.occupationCode",
      findKeysByValue(masterData.occupationDetails, values.member?.occupation)
    );
  }, [values.member?.occupation]);

  useEffect(() => {
    if (
      values.member?.relationship &&
      masterData?.jointRelationshipDetails &&
      values.member?.genderCode
    ) {
      const relationshipItem = masterData.jointRelationshipDetails[values.member.genderCode].find(
        (item: { label: string }) => item.label === values.member?.relationship
      );
      if (relationshipItem) {
        setFieldValue("member.relationshipCode", relationshipItem.value);
      }
    }
  }, [
    values.member?.relationship,
    masterData?.jointRelationshipDetails,
    values.member?.genderCode
  ]);

  useEffect(() => {
    setFieldValue(
      "member.genderCode",
      findKeysByValue(masterData.genderDetails, values.member?.gender)
    );
  }, [values.member?.gender]);

  useEffect(() => {
    setFieldValue(
      "member.nationalityCode",
      findKeysByValue(NationalityOptions.nationality, values.member?.nationality)
    );
  }, [values.member?.nationality]);

  useEffect(() => {
    if (values.member?.nationalityCode === "RES") {
      setFieldValue("member.countryOfResidence", "");
      setFieldValue("member.countryOfResidenceCode", "");
      setFieldValue("member.cityOfResidence", "");
      setFieldValue("member.dateOfLastEntry", "");
    }
    if (["OT", "SE"].includes(values.member?.occupationCode!)) {
      setFieldValue("member.designation", "");
    }
    if (["SE"].includes(values.member?.occupationCode!)) {
      setFieldValue("member.nameOfOrganization", "");
    }
    if (!["OT"].includes(values.member?.occupationCode!)) {
      setFieldValue("member.subOccupation", "");
      setFieldValue("member.subOccupationCode", "");
    }
  }, [values.member?.nationalityCode, values.member?.occupationCode]);

  const handleChangeMobile = (event: any) => {
    const { name, value } = event.target;
    if (name === "member.mobileNo" && value !== jointLifeMemberDetails.mobileNo) {
      setFieldValue("member.telePhoneNo", jointLifeMemberDetails.mobileNo);
    }
    setFieldValue(name, value);
  };

  const syncFieldCode = (
    field: string,
    value: string | undefined,
    codeField: string,
    dataSource: MasterData
  ) => {
    setFieldValue(`member.${codeField}`, findKeysByValue(dataSource, value));
  };

  useEffect(() => {
    syncFieldCode(
      "maritalStatus",
      values.member?.maritalStatus,
      "maritalStatusCode",
      masterData.maritalstatusDetails
    );
    syncFieldCode(
      "educationQualification",
      values.member?.educationQualification,
      "educationQualificationCode",
      masterData.educationDetails
    );
    syncFieldCode(
      "occupation",
      values.member?.occupation,
      "occupationCode",
      masterData.occupationDetails
    );
    syncFieldCode("gender", values.member?.gender, "genderCode", masterData.genderDetails);

    setFieldValue(
      "member.nationalityCode",
      findKeysByValue(NationalityOptions.nationality, values.member?.nationality)
    );
  }, [
    values.member?.maritalStatus,
    values.member?.educationQualification,
    values.member?.occupation,
    values.member?.gender,
    values.member?.nationality,
    masterData
  ]);

  useLayoutEffect(() => {
    if (!values.member?.genderCode || !masterData?.salutationDetails) return;
    const genderCode = values?.member?.genderCode;
    const genderSpecificTitles = masterData?.salutationDetails[genderCode];
    if (genderSpecificTitles?.length) {
      setFieldValue(
        "member.title",
        jointLifeMemberDetails?.title
          ? jointLifeMemberDetails?.title
          : genderSpecificTitles[0].label
      );
      setFieldValue(
        "member.titleCode",
        jointLifeMemberDetails?.titleCode
          ? jointLifeMemberDetails?.titleCode
          : genderSpecificTitles[0].value
      );
    }
  }, [values.member?.genderCode, masterData.salutationDetails, setFieldValue]);

  useEffect(() => {
    if (values.member?.nationalityCode === "RES") {
      [
        "countryOfResidence",
        "countryOfResidenceCode",
        "cityOfResidence",
        "dateOfLastEntry"
      ].forEach((field) => {
        setFieldValue(`member.${field}`, "");
      });
    }
    if (["OT", "SE"].includes(values.member?.occupationCode || "")) {
      setFieldValue("member.designation", "");
    }
    if (!["OT"].includes(values.member?.occupationCode || "")) {
      setFieldValue("member.subOccupation", "");
      setFieldValue("member.subOccupationCode", "");
    }
  }, [values.member?.nationalityCode, values.member?.occupationCode, setFieldValue]);

  useEffect(() => {
    setFieldValue(
      "member.annualIncome",
      values.jointHousewifeFlag ? "" : jointLifeMemberDetails.annualIncome
    );
  }, [values.jointHousewifeFlag, jointLifeMemberDetails.annualIncome, setFieldValue]);

  useEffect(() => {
    if (values.member?.electronicAccount === "No") {
      setFieldValue("member.eiaNumber", "");
    }
  }, [values.member?.electronicAccount, setFieldValue]);

  useEffect(() => {
    const current = values.member?.occupationCode || "";
    const prev = prevOccupationRef.current || "";
    const businessTypes = ["BO", "SE"];
    const isBusinessType = businessTypes.includes(current);

    if (!isBusinessType) {
      if (values.member?.businessOwner !== false) {
        setFieldValue("member.businessOwner", false, false);
      }
    }

    if (current !== prev && isBusinessType) {
      setFieldValue("member.businessOwner", true, false);
    }
    prevOccupationRef.current = current;
  }, [values.member?.occupationCode]);

  const isFieldVisible = (fieldName: keyof typeof memberDetailsSchema): boolean => {
    const fieldConfig = memberDetailsSchema[fieldName];
    if (!fieldConfig.visible) return false;

    switch (fieldName) {
      case "countryOfResidence":
      case "cityOfResidence":
      case "dateOfLastEntry":
        return !["RES", ""].includes(values.member?.nationalityCode!);
      case "subOccupation":
        return values.member?.occupationCode === "OT";
      case "designation":
        return !["SE", "OT", ""].includes(values.member?.occupationCode || "");
      case "UIDNo":
      case "electronicAccount":
        return [PRODUCT_ID.GHS, PRODUCT_ID.GHS_IDBI, PRODUCT_ID.GCS].includes(productId!);
      case "eiaNumber":
        return values.member?.electronicAccount === "Yes";
      case "nameOfOrganization":
        return !(partners.JIO === partnerId && ["SE", ""].includes(values.member.occupationCode!));
      case "businessOwner":
        return ["BO", "SE"].includes(values.member.occupationCode!);
      default:
        return true;
    }
  };

  const salutationOptions = useMemo(() => {
    if (!masterData?.salutationDetails) return [];
    if (values.member?.genderCode) {
      return (
        masterData.salutationDetails[values.member.genderCode]?.map(
          (item: { label: string; value: string }) => item.label
        ) || []
      );
    }
    return Object.values(masterData.salutationDetails).flatMap((genderTitles: any) =>
      genderTitles.map((item: { label: string }) => item.label)
    );
  }, [values.member?.genderCode, masterData?.salutationDetails]);

  const relationshipOptions = useMemo(() => {
    if (!masterData?.jointRelationshipDetails) return [];
    if (values.member?.genderCode) {
      return (
        masterData.jointRelationshipDetails[values.member.genderCode]?.map(
          (item: { label: string; value: string }) => item.label
        ) || []
      );
    }
    return [];
  }, [values.member?.genderCode, masterData?.jointRelationshipDetails]);

  const renderField = (fieldName: any) => {
    const fieldConfig = memberDetailsSchema[fieldName];
    const fieldPath = `member.${fieldName}`;
    const error = getIn(errors, fieldPath);
    const touch = getIn(touched, fieldPath);
    const value = getIn(values, fieldPath) || fieldConfig.defaultValue;

    if (!isFieldVisible(fieldName)) return null;

    const commonProps = {
      key: fieldName,
      name: fieldPath,
      label: fieldConfig.label,
      value: value as string,
      error,
      touched: touch,
      disabled: fieldConfig.disabled,
      requiredStar: fieldConfig.required,
      onBlur: handleBlur,
      setFieldTouched,
      onChange: handleChange
    };

    switch (fieldName) {
      case "gender":
        return (
          <CustomRadioButton
            {...commonProps}
            className={`${
              values.member?.title === "Others"
                ? "col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-3"
                : "col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-3"
            }`}
            options={genderDataList}
            setFieldValue={setFieldValue}
            defaultValue={value}
          />
        );
      case "dob":
      case "dateOfLastEntry":
        return (
          <CustomDatePicker
            {...commonProps}
            disableFuture
            setFieldValue={setFieldValue}
            minDate={
              fieldName === "dateOfLastEntry" && values.member?.dob
                ? dayjs(new Date(values.member.dob.split("/").reverse().join("-")))
                : undefined
            }
          />
        );
      case "title":
        return (
          <CustomSelect
            {...commonProps}
            options={salutationOptions}
            value={values.member?.title || ""}
            onChange={(e) => {
              handleChange(e);
              const selectedTitle = e.target.value;
              if (masterData?.salutationDetails && values.member?.genderCode) {
                const titleItem = masterData.salutationDetails[values.member.genderCode].find(
                  (item: { label: string }) => item.label === selectedTitle
                );
                if (titleItem) {
                  setFieldValue("member.titleCode", titleItem.value);
                }
              }
            }}
          />
        );
      case "maritalStatus":
        return <CustomSelect {...commonProps} options={maritalStatusDataList} />;
      case "nationality":
        return <CustomSelect {...commonProps} options={OPTIONS.nationality} />;
      case "educationQualification":
        return <CustomSelect {...commonProps} options={educationDataList} />;
      case "occupation":
        return <CustomSelect {...commonProps} options={occupationDataList} />;
      case "natureOfWork":
        return <CustomSelect {...commonProps} options={OPTIONS.natureOfWork} />;
      case "relationship":
        return (
          <Tooltip
            title="Relationship With Primary Member To Be Insured *"
            placement="top"
            classes={{ tooltipPlacementTop: "!m-0.5" }}
            PopperProps={{ className: "!z-0" }}
          >
            <div>
              <CustomSelect
                {...commonProps}
                label="Relationship with primary..."
                options={relationshipOptions}
                value={values.member?.relationship || ""}
                onChange={(e) => {
                  handleChange(e);
                  const selectedRelationship = e.target.value;
                  if (masterData?.jointRelationshipDetails && values.member?.genderCode) {
                    const relationshipItem = masterData.jointRelationshipDetails[
                      values.member.genderCode
                    ].find((item: { label: string }) => item.label === selectedRelationship);
                    if (relationshipItem) {
                      setFieldValue("member.relationshipCode", relationshipItem.value);
                    }
                  }
                }}
              />
            </div>
          </Tooltip>
        );
      case "electronicAccount":
        return (
          <Tooltip
            title="Do you have Electronic Insurance Account *"
            placement="top"
            classes={{ tooltipPlacementTop: "!m-0.5" }}
            PopperProps={{ className: "!z-0" }}
          >
            <div>
              <CustomSelect
                {...commonProps}
                label="Do you have Electronic ..."
                options={OPTIONS.electronicAccount}
              />
            </div>
          </Tooltip>
        );
      case "subOccupation":
        return (
          <CustomAutocomplete
            {...commonProps}
            onChange={handleSubOccupationChange}
            options={subOccupationOptions}
            setFieldValue={setFieldValue}
          />
        );
      case "countryOfResidence":
        return (
          <CustomAutocomplete
            {...commonProps}
            onChange={handleCountryChange}
            options={countryOptions}
            setFieldValue={setFieldValue}
          />
        );
      case "annualIncome":
        return (
          <CustomTextField
            handleBlur={handleBlur}
            {...commonProps}
            requiredStar={!values.jointHousewifeFlag}
            disabledField={values.jointHousewifeFlag}
            onChange={(event) => handelIndianCurrencyChange(event, { setFieldValue })}
            value={onChangeIndianCurrency(value)}
          />
        );
      case "panNo":
        return (
          <CustomTextField
            handleBlur={handleBlur}
            {...commonProps}
            onChange={(e) => handleUppercaseChange(e, handleChange)}
          />
        );
      case "mobileNo":
        return (
          <CustomTextField handleBlur={handleBlur} {...commonProps} onChange={handleChangeMobile} />
        );
      case "businessOwner":
        return (
          ["BO", "SE"].includes(values.member?.occupationCode!) && (
            <div className="flex flex-col">
              <CustomCheckBox
                label="If Business owner or self employed, is SME / MSME"
                name="member.businessOwner"
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                checked={values.member?.businessOwner}
              />
              <div className="text-xs text-gray-500 mt-1">
                This selection is for internal reference only. Please update if you do not fall
                under SME category.
              </div>
            </div>
          )
        );
      default:
        return <CustomTextField handleBlur={handleBlur} {...commonProps} />;
    }
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
        {(Object.keys(memberDetailsSchema) as Array<keyof typeof memberDetailsSchema>).map(
          (fieldName) => renderField(fieldName)
        )}
      </div>
    </div>
  );
}

export default memo(JointMemberDetails);
